import { useMemo, useState } from 'react'
import Styles from './Stock.module.scss'
import { useUnsplashImages } from 'hooks/queries/useUnsplashImages'
import { useDebounce } from 'hooks/useDebounce'
import { SearchField } from '../SearchField/SearchField'
import { Grid } from '../Grid/Grid'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ScrollContainer } from '../ScrollContainer/ScrollContainer'
import { StockContentProps, StockProps } from './Types'
import { usePixabayElements } from 'hooks/queries/designer/usePixabayElements'
import { SelectedImage } from 'types'
import { getVideoMimeType } from 'components/Design/UploadsTab/components/StockVideosGallery'
import { random, sample } from 'lodash'
import { useImagePickerContext } from 'components/ImagePickerModal/ImagePickerContext'
import { Spinner } from 'common/Spinner/Spinner'
import { ImageThumbnail } from '../MediaFileThumbnail/ImageThumbnail'
import { useStockVideos, VIDEO_CATEGORIES } from 'hooks/queries/designer/useStockVideos'

const Content = ({
  mediaFiles,
  hasNextPage,
  fetchNextPage,
  query,
  setQuery,
  isLoading,
}: StockProps & StockContentProps) => {
  const { selectedImages, toggleSelectedImage } = useImagePickerContext()
  const showInfiniteScroll = !isLoading && hasNextPage !== undefined

  return (
    <div className={Styles.stock} data-testid="stock-provider">
      <SearchField value={query} onChange={setQuery} />

      {mediaFiles.length === 0 && (
        <div className={Styles.infiniteLoader} aria-label="Loading...">
          <Spinner />
        </div>
      )}

      {mediaFiles.length > 0 && (
        <ScrollContainer>
          <Grid.Root breakpointCols={4}>
            {mediaFiles.map((image) => {
              const isSelected = selectedImages.some((i) => i.url === image.url)

              return (
                <Grid.Thumbnail key={image.unsplash?.id || image.pixabay?.id || image.id}>
                  <ImageThumbnail
                    image={image}
                    onToggleSelect={toggleSelectedImage}
                    isSelected={isSelected}
                    label={image.unsplash?.authorName}
                    duration={image.duration}
                  />
                </Grid.Thumbnail>
              )
            })}
          </Grid.Root>
          {showInfiniteScroll && (
            <div className={Styles.infiniteLoader}>
              <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} />
            </div>
          )}
        </ScrollContainer>
      )}
    </div>
  )
}

export const StockVideo = (props: StockProps) => {
  const { initialQuery } = useImagePickerContext()
  const [query, setQuery] = useState(initialQuery || '')

  const debouncedQuery = useDebounce(query, 500)

  const category = useMemo(() => sample(VIDEO_CATEGORIES), [])

  const { 
    data: stockVideosData, 
    isLoading: isStockVideosLoading,
    hasNextPage: stockVideosHasNextPage,
    fetchNextPage: stockVideosFetchNextPage
  } = useStockVideos({
    query: debouncedQuery,
    pageSize: 24,
    videoType: 'all',
    category: !debouncedQuery ? category : undefined,
  })

  const videos = (stockVideosData?.pages || []).flatMap((page) => page.videos || [])
  const videoFiles = videos.map((video) => ({
    id: video.id,
    url: video.videos.tiny.url,
    source_url: video.videos.tiny.url,
    thumbnail_url: video.videos.tiny.thumbnail,
    duration: video.duration,
    variants: [],
    mime_type: video.videos.tiny.mime_type ?? getVideoMimeType(video.videos.tiny.url),
    title: video.id,
    preview_variant_url: video.videos.tiny.url,
  }))

  const hasNextPage = stockVideosHasNextPage
  const fetchNextPage = () => {
    if (stockVideosHasNextPage) stockVideosFetchNextPage()
  }

  return (
    <Content
      {...props}
      query={query}
      setQuery={setQuery}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={isStockVideosLoading}
      mediaFiles={videoFiles}
    />
  )
}

export const StockWithVideo = (props: StockProps) => {
  const { initialQuery } = useImagePickerContext()
  const [query, setQuery] = useState(initialQuery || '')

  const debouncedQuery = useDebounce(query, 500)
  const {
    data: unsplashData,
    fetchNextPage: unsplashFetchNextPage,
    hasNextPage: unsplashHasNextPage,
    isLoading: unsplashIsLoading,
  } = useUnsplashImages({ query: debouncedQuery, perPage: 24 })

  const unsplashResults = (unsplashData?.pages || []).flatMap((page) =>
    page?.type === 'success' ? (page.data.results || page.data) ?? [] : [],
  )

  const unsplashImages = unsplashResults.map((item) => {
    return {
      id: item.id,
      url: item.urls.regular,
      height: item.height,
      width: item.width,
      unsplash: {
        authorUsername: item.user.username,
        authorName: item.user.name,
        id: item.id,
        query,
      },
    }
  })

  const category = useMemo(() => sample(VIDEO_CATEGORIES), [])
  const {
    data: stockVideosData,
    fetchNextPage: stockVideosFetchNextPage,
    hasNextPage: stockVideosHasNextPage,
    isLoading: stockVideosIsLoading,
  } = useStockVideos({
    query: debouncedQuery,
    category: !debouncedQuery ? category : undefined,
    pageSize: 24,
    videoType: 'all',
  })

  const videos = (stockVideosData?.pages || []).flatMap((page) => page.videos || [])
  const videoFiles = videos.map((video) => ({
    id: video.id,
    url: video.videos.tiny.url,
    source_url: video.videos.tiny.url,
    thumbnail_url: video.videos.tiny.thumbnail,
    duration: video.duration,
    variants: [],
    mime_type: video.videos.tiny.mime_type ?? getVideoMimeType(video.videos.tiny.url),
    title: video.id,
    preview_variant_url: video.videos.tiny.url,
  }))

  const mediaFiles = useMemo(() => {
    const files: SelectedImage[] = []
    const maxLength = Math.max(unsplashImages.length, videoFiles.length)

    for (let i = 0; i < maxLength; i++) {
      const order = random(2)
      const mediaFileIds = files.map((mf) => mf.id || mf.unsplash?.id)

      if (order === 0) {
        if (i < unsplashImages.length && !mediaFileIds.includes(unsplashImages[i].id)) {
          files.push(unsplashImages[i])
        }
        if (i < videoFiles.length && !mediaFileIds.includes(videoFiles[i].id)) {
          files.push(videoFiles[i])
        }
      } else {
        if (i < videoFiles.length && !mediaFileIds.includes(videoFiles[i].id)) {
          files.push(videoFiles[i])
        }
        if (i < unsplashImages.length && !mediaFileIds.includes(unsplashImages[i].id)) {
          files.push(unsplashImages[i])
        }
      }
    }

    return files
  }, [unsplashImages.length, videoFiles.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasNextPage = unsplashHasNextPage || stockVideosHasNextPage
  const fetchNextPage = () => {
    if (unsplashHasNextPage) unsplashFetchNextPage()
    if (stockVideosHasNextPage) stockVideosFetchNextPage()
  }

  return (
    <Content
      {...props}
      query={query}
      setQuery={setQuery}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={unsplashIsLoading || stockVideosIsLoading}
      mediaFiles={mediaFiles}
    />
  )
}

export const StockWithoutVideo = (props: StockProps) => {
  const { initialQuery } = useImagePickerContext()
  const [query, setQuery] = useState(initialQuery || '')

  const debouncedQuery = useDebounce(query, 500)
  const {
    data: unsplashData,
    fetchNextPage: unsplashFetchNextPage,
    hasNextPage: unsplashHasNextPage,
    isLoading: unsplashIsLoading,
  } = useUnsplashImages({ query: debouncedQuery, perPage: 24 })
  const {
    data: pixabayData,
    fetchNextPage: pixabayFetchNextPage,
    hasNextPage: pixabayHasNextPage,
    isLoading: pixabayIsLoading,
  } = usePixabayElements({ query: debouncedQuery, pageSize: 24, imageType: 'all' })

  const unsplashResults = (unsplashData?.pages || []).flatMap((page) =>
    page?.type === 'success' ? (page.data.results || page.data) ?? [] : [],
  )

  const unsplashImages = unsplashResults.map((item) => {
    return {
      url: item.urls.regular,
      height: item.height,
      width: item.width,
      unsplash: {
        authorUsername: item.user.username,
        authorName: item.user.name,
        id: item.id,
        query,
      },
    }
  })

  const pixabayResults = (pixabayData?.pages || []).flatMap((page) => page.images || [])
  const pixabayImages = pixabayResults.map((item) => {
    return {
      url: item.largeImageURL,
      height: item.imageHeight,
      width: item.imageWidth,
      pixabay: {
        id: item.id,
      },
    }
  })

  const mediaFiles = useMemo(() => {
    const files: SelectedImage[] = []
    const maxLength = Math.max(unsplashImages.length, pixabayImages.length)

    for (let i = 0; i < maxLength; i++) {
      if (i < unsplashImages.length) {
        files.push(unsplashImages[i])
      }
      if (i < pixabayImages.length && !!debouncedQuery) {
        files.push(pixabayImages[i])
      }
    }

    return files
  }, [debouncedQuery, pixabayImages, unsplashImages])

  const hasNextPage = unsplashHasNextPage || pixabayHasNextPage
  const fetchNextPage = () => {
    if (unsplashHasNextPage) unsplashFetchNextPage()
    if (pixabayHasNextPage) pixabayFetchNextPage()
  }

  return (
    <Content
      {...props}
      query={query}
      setQuery={setQuery}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isLoading={unsplashIsLoading || pixabayIsLoading}
      mediaFiles={mediaFiles}
    />
  )
}

export const Stock = ({ withVideo, onlyVideo, ...props }: StockProps) => {
  if (onlyVideo) return <StockVideo {...props} />
  if (withVideo) return <StockWithVideo {...props} />

  return <StockWithoutVideo {...props} />
}
